import { useState, useEffect, useRef, useCallback } from 'react';
import { friendlyMessage } from "@/lib/errors";
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Eye, EyeOff, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/components/AuthProvider';
import {
  PhoneAuthFlow,
  type PhoneAuthFlowHandle,
  type PhoneAuthStep,
} from '@/components/PhoneAuthFlow';
import { authApi } from '@/lib/api/services/auth';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { AuthShell } from '@/components/AuthShell';
import { isNewSignupPending } from '@/lib/onboardingCompletion';
import { ONBOARDING_V2_PREFLIGHT_PATH } from '@/components/onboarding-v2/onboardingV2Routes';

export default function Auth() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [signInMode, setSignInMode] = useState<'password' | 'otp'>('otp');
  const [phoneStep, setPhoneStep] = useState<PhoneAuthStep>('phone');
  const [otpPhoneDisplay, setOtpPhoneDisplay] = useState('');
  const phoneAuthRef = useRef<PhoneAuthFlowHandle>(null);

  const [requiresTotp, setRequiresTotp] = useState(false);
  const [totpUserId, setTotpUserId] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [forgotPassword, setForgotPassword] = useState(false);
  const [fpEmail, setFpEmail] = useState('');
  const [fpLoading, setFpLoading] = useState(false);
  const [fpError, setFpError] = useState('');
  const [fpSuccess, setFpSuccess] = useState(false);
  const { user, login, verifyLoginTotp } = useAuth();

  useEffect(() => {
    if (user) {
      if ((user as any)?.is_agency) {
        navigate('/home/agency/dashboard');
      } else if (isNewSignupPending()) {
        navigate(ONBOARDING_V2_PREFLIGHT_PATH);
      } else {
        navigate('/home');
      }
    }
  }, [user, navigate]);

  const handlePhoneStepChange = useCallback((step: PhoneAuthStep, phone?: string) => {
    setPhoneStep(step);
    if (phone) setOtpPhoneDisplay(phone);
  }, []);

  const redirectAfterLogin = () => {
    localStorage.setItem('showBillingDialog', 'true');
    setTimeout(() => {
      const currentUser = user || JSON.parse(localStorage.getItem('user') || '{}');
      if (currentUser?.is_agency) {
        navigate('/home/agency/dashboard');
      } else {
        navigate('/home');
      }
    }, 100);
  };

  const normalizeLoginIdentifier = (raw: string) => {
    const v = (raw || '').trim();
    if (!v) return v;
    if (v.includes('@')) return v.toLowerCase();
    if (v.startsWith('+')) return v.replace(/\s+/g, '');
    const digits = v.replace(/\D/g, '');
    if (digits.length >= 6) return `+91${digits}`;
    return v;
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();

    let hasError = false;
    if (!email.trim()) {
      setEmailError('Please enter your email or phone number');
      hasError = true;
    }
    if (!password) {
      setPasswordError('Please enter your password');
      hasError = true;
    }
    if (hasError) return;

    setLoading(true);
    setError('');

    try {
      const result = await login(normalizeLoginIdentifier(email), password);

      if (result.success) {
        if (result.requires_totp && result.userId) {
          setRequiresTotp(true);
          setTotpUserId(result.userId);
          setLoading(false);
          return;
        }

        redirectAfterLogin();
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (error: any) {
      setError(friendlyMessage(error, "We couldn't log you in. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await verifyLoginTotp(totpUserId, totpCode);

      if (result.success) {
        localStorage.setItem('showBillingDialog', 'true');
        setTimeout(() => {
          const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
          if (currentUser?.is_agency) {
            navigate('/home/agency/dashboard');
          } else {
            navigate('/home');
          }
        }, 100);
      } else {
        setError(result.error || 'Invalid code');
      }
    } catch (error: any) {
      setError(friendlyMessage(error, "We couldn't verify your code. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const exitForgotPassword = () => {
    setForgotPassword(false);
    setFpError('');
    setFpSuccess(false);
    setFpEmail('');
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setFpLoading(true);
    setFpError('');

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(fpEmail)) {
      setFpError('Please enter a valid email address');
      setFpLoading(false);
      return;
    }

    try {
      const result = await authApi.requestPasswordReset({ email: fpEmail });
      if (result.error) {
        setFpError(friendlyMessage(result.error));
      } else {
        setFpSuccess(true);
        toast({
          title: 'Email sent!',
          description: 'Check your email for password reset instructions.',
        });
      }
    } catch (error: any) {
      setFpError(friendlyMessage(error, "We couldn't send the reset email. Please try again."));
    } finally {
      setFpLoading(false);
    }
  };

  return (
    <AuthShell>
          {forgotPassword ? (
            <>
              <button
                type="button"
                aria-label="Back"
                onClick={exitForgotPassword}
                className="mb-3 -ml-1 inline-flex h-9 w-fit items-center gap-1.5 rounded-lg px-1 text-sm font-medium text-gray-700 transition-colors hover:text-gray-900"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>

              <h1 className="text-2xl font-bold text-gray-900">
                {fpSuccess ? 'Check your email' : 'Forgot password?'}
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                {fpSuccess ? (
                  <>
                    We've sent a password reset link to{' '}
                    <span className="font-semibold">{fpEmail}</span>.
                  </>
                ) : (
                  "Enter your email address and we'll send you a link to reset your password."
                )}
              </p>

              {fpSuccess ? (
                <div className="mt-6 space-y-4">
                  <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4">
                    <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-green-500" />
                    <div className="text-sm text-gray-600">
                      <p>If you don't see the email, check your spam folder.</p>
                      <p className="mt-1 text-xs text-gray-500">The reset link will expire in 1 hour.</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full !h-12"
                    onClick={() => { setFpSuccess(false); setFpEmail(''); }}
                  >
                    Send another email
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={exitForgotPassword}
                  >
                    Back to login
                  </Button>
                </div>
              ) : (
                <>
                  {fpError && (
                    <Alert variant="destructive" className="mt-4">
                      <AlertDescription>{fpError}</AlertDescription>
                    </Alert>
                  )}
                  <form onSubmit={handleForgotPassword} noValidate className="mt-6 space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="fp-email">Email</Label>
                      <Input
                        id="fp-email"
                        type="email"
                        placeholder="your@email.com"
                        value={fpEmail}
                        onChange={(e) => { setFpEmail(e.target.value); if (fpError) setFpError(''); }}
                        required
                        autoFocus
                        disabled={fpLoading}
                        className="h-12"
                      />
                    </div>
                    <Button type="submit" className="w-full !h-12" disabled={fpLoading || !fpEmail.trim()}>
                      {fpLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {fpLoading ? 'Sending...' : 'Send reset link'}
                    </Button>
                  </form>
                </>
              )}
            </>
          ) : requiresTotp ? (
            <>
              <h1 className="text-2xl font-bold text-gray-900">Two-factor authentication</h1>
              <p className="mt-1 text-sm text-gray-500">Enter the code from your authenticator app.</p>
              {error && (
                <Alert variant="destructive" className="mt-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <form onSubmit={handleVerifyTotp} className="mt-6 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="totpCode">Verification code</Label>
                  <Input
                    id="totpCode"
                    type="text"
                    placeholder="123456"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="h-12 text-center text-lg tracking-widest"
                    required
                    autoFocus
                  />
                </div>
                <Button type="submit" className="w-full !h-12" disabled={loading || totpCode.length !== 6}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Verify
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setRequiresTotp(false);
                    setTotpUserId('');
                    setTotpCode('');
                    setError('');
                  }}
                >
                  Back to login
                </Button>
              </form>
            </>
          ) : (
            <>
              {signInMode === 'otp' && phoneStep !== 'phone' && (
                <button
                  type="button"
                  aria-label="Back"
                  onClick={() => phoneAuthRef.current?.back()}
                  className="mb-3 -ml-1 inline-flex h-9 w-fit items-center gap-1.5 rounded-lg px-1 text-sm font-medium text-gray-700 transition-colors hover:text-gray-900"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
              )}

              <h1 className="text-2xl font-bold text-gray-900">
                {signInMode === 'password'
                  ? 'Welcome back'
                  : phoneStep === 'otp'
                    ? 'Verify your account'
                    : 'Log in or sign up'}
              </h1>

              <p className="mt-1 text-sm text-gray-500">
                {signInMode === 'otp'
                  ? (phoneStep === 'otp'
                      ? (
                          <>
                            We've sent a 6-digit verification code to{' '}
                            <span className="font-semibold">{otpPhoneDisplay}</span> on WhatsApp.
                          </>
                        )
                      : "Enter your phone number — we'll send a code on WhatsApp.")
                  : 'Login with your email or phone and password.'}
              </p>

              {error && (
                <Alert variant="destructive" className="mt-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="mt-6">
                {signInMode === 'otp' ? (
                  <PhoneAuthFlow
                    ref={phoneAuthRef}
                    onStepChange={handlePhoneStepChange}
                    onRequiresTotp={(userId) => { setRequiresTotp(true); setTotpUserId(userId); }}
                    onSuccess={redirectAfterLogin}
                    onSwitchToPassword={(message) => { setSignInMode('password'); setError(message); }}
                  />
                ) : (
                  <form onSubmit={handleSignIn} noValidate className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email/phone</Label>
                      <Input
                        id="email"
                        type="text"
                        placeholder="Enter email or phone number"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (emailError) setEmailError('');
                        }}
                        aria-invalid={!!emailError}
                        required
                        className={cn('h-12', emailError && 'border-red-400 focus-visible:ring-red-400')}
                      />
                      {emailError && <p className="text-xs text-red-600">{emailError}</p>}
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password">Password</Label>
                        <button
                          type="button"
                          onClick={() => { setForgotPassword(true); setError(''); }}
                          className="text-xs text-primary hover:underline"
                        >
                          Forgot password?
                        </button>
                      </div>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Your password"
                          value={password}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            if (passwordError) setPasswordError('');
                          }}
                          aria-invalid={!!passwordError}
                          required
                          className={cn('h-12 pr-10', passwordError && 'border-red-400 focus-visible:ring-red-400')}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                      {passwordError && <p className="text-xs text-red-600">{passwordError}</p>}
                    </div>
                    <Button type="submit" className="w-full !h-12 font-bold text-[16px]" disabled={loading}>
                      {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Login
                    </Button>
                  </form>
                )}
              </div>

              {(signInMode === 'password' || phoneStep === 'phone') && (
                <p className="mt-4 text-center text-sm text-gray-500">
                  {signInMode === 'otp' ? (
                    <>
                      Prefer email?{' '}
                      <button
                        type="button"
                        onClick={() => { setSignInMode('password'); setError(''); }}
                        className="font-semibold text-[var(--secondary-color)] hover:underline"
                      >
                        Login with password
                      </button>
                    </>
                  ) : (
                    <>
                      Prefer phone?{' '}
                      <button
                        type="button"
                        onClick={() => { setSignInMode('otp'); setError(''); }}
                        className="font-semibold text-[var(--secondary-color)] hover:underline"
                      >
                        Login with OTP
                      </button>
                    </>
                  )}
                </p>
              )}
            </>
          )}
    </AuthShell>
  );
}
